import { ApiRequestConfig, getDeliveryMinPrice, getDeliveryStoreIds, getPickupAddress } from 'api';
import {
  FAVORITE_DELIVERY_TYPE,
  FAVORITE_DELIVERY_TYPE_COOKIE,
  PRIORITY_STORE_IDS_COOKIE,
} from 'common/constants';
import i18n from 'i18next';
import {
  CheckoutAddress,
  CheckoutInfo,
  FavoriteDeliveryType,
  isFavoriteDeliveryType,
} from 'types/checkout-info';
import {
  FavoriteAddressDetails,
  FavoriteAddressState,
  ApplyFavoriteAddressPayload,
} from 'types/favorite-address';
import { PickupAddressParams } from 'types/pickup-address';
import { applyCheckoutInfoDeletions } from './checkout-info';

type LoadFavoriteDataParams = {
  cookies: Record<string, string>;
  checkoutInfo: CheckoutInfo | null;
  apiConfig: ApiRequestConfig;
};

type LoadDeliveryStoreIdsParams = {
  checkoutInfo: CheckoutInfo | null;
  apiConfig: ApiRequestConfig;
};

type LoadFavoriteSheetDataParams = {
  checkoutInfo: CheckoutInfo | null;
  regionId?: string;
  address: FavoriteAddressDetails | null;
  apiConfig: ApiRequestConfig;
};

const emptyState = (patch: Partial<FavoriteAddressState> = {}): FavoriteAddressState => ({
  deliveryType: FAVORITE_DELIVERY_TYPE.REGION,
  address: null,
  storeIds: null,
  priorityStoreIds: null,
  shouldResetToRegion: false,
  unavailable: false,
  minPriceText: null,
  sheetLoaded: false,
  ...patch,
});

const formatHomeDeliveryAddress = ({ city, street, house, title }: CheckoutAddress): string => {
  if (!city && !street) {
    return title;
  }

  const location = street || city;

  return house ? `${location}, ${house}` : location;
};

const getFavoriteDeliveryTypeFromCookie = (
  cookies: Record<string, string>,
  checkoutInfo: CheckoutInfo | null,
): FavoriteDeliveryType => {
  const cookieType = cookies[FAVORITE_DELIVERY_TYPE_COOKIE];

  const lsType = checkoutInfo?.deliveryType;

  if (lsType && lsType !== FAVORITE_DELIVERY_TYPE.REGION && isFavoriteDeliveryType(lsType)) {
    return lsType;
  }

  if (cookieType) {
    if (cookieType === FAVORITE_DELIVERY_TYPE.REGION) {
      return FAVORITE_DELIVERY_TYPE.REGION;
    }

    return isFavoriteDeliveryType(cookieType) ? cookieType : FAVORITE_DELIVERY_TYPE.REGION;
  }

  return FAVORITE_DELIVERY_TYPE.REGION;
};

const hasPickupAddress = (address: FavoriteAddressDetails | null): boolean =>
  Boolean(address?.storeName || address?.storeAddress || address?.pickupPointAddress);

const buildPickupParams = (checkoutInfo: CheckoutInfo): PickupAddressParams | null => {
  const { storeId, pointId } = checkoutInfo;

  if (storeId && pointId) return { storeId, pointId };
  if (storeId) return { storeId };
  if (pointId) return { pointId };

  return null;
};

export const getDeliveryAddress = (
  type: FavoriteDeliveryType,
  address: FavoriteAddressDetails | null,
  checkoutInfo: CheckoutInfo | null,
): string | undefined => {
  switch (type) {
    case FAVORITE_DELIVERY_TYPE.HOME_DELIVERY:
      return checkoutInfo?.address?.title ? formatHomeDeliveryAddress(checkoutInfo.address) : undefined;
    case FAVORITE_DELIVERY_TYPE.PICKUP:
      return address?.storeName;
    case FAVORITE_DELIVERY_TYPE.PICKUP_POINT:
      return address?.pickupPointAddress;
    default:
      return;
  }
};

const buildAddress = (
  sheetAddress: FavoriteAddressDetails | null,
  deliveryType: FavoriteDeliveryType,
  checkoutInfo: CheckoutInfo | null,
): FavoriteAddressDetails | null => {
  const deliveryAddress = getDeliveryAddress(deliveryType, sheetAddress, checkoutInfo);

  if (!sheetAddress) {
    return deliveryAddress ? { deliveryAddress } : null;
  }

  return deliveryAddress ? { ...sheetAddress, deliveryAddress } : sheetAddress;
};

export const getFavoriteDeliveryType = (state: FavoriteAddressState): FavoriteDeliveryType => {
  if (
    state.unavailable ||
    state.deliveryType === FAVORITE_DELIVERY_TYPE.REGION ||
    !state.address?.deliveryAddress
  ) {
    return FAVORITE_DELIVERY_TYPE.REGION;
  }

  return state.deliveryType;
};

const formatDeliveryMinPrice = (minPrice: string | number): string | null => {
  const price = Number(minPrice);
  if (!Number.isFinite(price)) return null;

  return i18n.t('header.main.favorite-address.address-menu.min-price', { price });
};

export const loadFavoriteData = async ({
  cookies,
  checkoutInfo,
  apiConfig,
}: LoadFavoriteDataParams): Promise<FavoriteAddressState> => {
  const deliveryType = getFavoriteDeliveryTypeFromCookie(cookies, checkoutInfo);
  const cookieStoreIds = cookies[PRIORITY_STORE_IDS_COOKIE]?.split(',').filter(Boolean);
  const needsStoreIds = !cookieStoreIds?.length && deliveryType === FAVORITE_DELIVERY_TYPE.HOME_DELIVERY;
  const needsPickupAddress =
    deliveryType === FAVORITE_DELIVERY_TYPE.PICKUP || deliveryType === FAVORITE_DELIVERY_TYPE.PICKUP_POINT;
  const pickupParams = needsPickupAddress && checkoutInfo ? buildPickupParams(checkoutInfo) : null;

  const [pickupAddress, storeIds] = await Promise.all([
    pickupParams ? getPickupAddress(pickupParams, apiConfig).catch(() => null) : Promise.resolve(null),
    needsStoreIds ? loadDeliveryStoreIds({ checkoutInfo, apiConfig }) : Promise.resolve(null),
  ]);

  const loadedState = emptyState({
    deliveryType,
    address: buildAddress(pickupAddress, deliveryType, checkoutInfo),
    storeIds,
    priorityStoreIds: cookieStoreIds?.length ? cookieStoreIds : storeIds,
  });

  return {
    ...loadedState,
    deliveryType: getFavoriteDeliveryType(loadedState),
  };
};

export const loadDeliveryStoreIds = async ({
  checkoutInfo,
  apiConfig,
}: LoadDeliveryStoreIdsParams): Promise<string[] | null> => {
  const latitude = Number(checkoutInfo?.address?.latitude);
  const longitude = Number(checkoutInfo?.address?.longitude);

  if (!latitude || !longitude) {
    return null;
  }

  const response = await getDeliveryStoreIds({ latitude, longitude }, apiConfig).catch(() => null);

  return response?.storeIds ?? null;
};

export const loadFavoriteSheetData = async ({
  checkoutInfo,
  regionId,
  address,
  apiConfig,
}: LoadFavoriteSheetDataParams) => {
  const pickupParams = !hasPickupAddress(address) && checkoutInfo ? buildPickupParams(checkoutInfo) : null;

  const [pickupAddress, minPrice] = await Promise.all([
    pickupParams ? getPickupAddress(pickupParams, apiConfig).catch(() => null) : Promise.resolve(null),
    regionId
      ? // Ошибки API не пробрасываются: sheet загружается частично при недоступности сервисов
        getDeliveryMinPrice({ regionId }, apiConfig).catch(() => null)
      : Promise.resolve(null),
  ]);

  return {
    pickupAddress: pickupAddress ?? null,
    minPriceText: minPrice !== null ? formatDeliveryMinPrice(minPrice) : null,
  };
};

export const mergeFavoriteSheetData = (
  state: FavoriteAddressState,
  sheet: Awaited<ReturnType<typeof loadFavoriteSheetData>>,
  checkoutInfo: CheckoutInfo | null,
): FavoriteAddressState => ({
  ...state,
  address: buildAddress({ ...state.address, ...sheet.pickupAddress }, state.deliveryType, checkoutInfo),
  minPriceText: sheet.minPriceText ?? state.minPriceText,
  sheetLoaded: true,
});

export const applyFavoriteAddressSelection = (
  state: FavoriteAddressState,
  deliveryType: FavoriteDeliveryType,
  checkoutInfo: CheckoutInfo | null,
): FavoriteAddressState => {
  const address = buildAddress(state.address, deliveryType, checkoutInfo);

  let priorityStoreIds: string[] | null = null;

  if (deliveryType === FAVORITE_DELIVERY_TYPE.HOME_DELIVERY) {
    priorityStoreIds = state.storeIds ?? [];
  } else if (deliveryType === FAVORITE_DELIVERY_TYPE.PICKUP && checkoutInfo?.storeId) {
    priorityStoreIds = [String(checkoutInfo.storeId)];
  }

  return {
    ...state,
    deliveryType,
    address,
    priorityStoreIds,
    shouldResetToRegion: false,
  };
};

const applyAddressDeletionsToState = (
  state: FavoriteAddressState,
  deletedTypes: ApplyFavoriteAddressPayload['deletedAddressTypes'],
): FavoriteAddressState => {
  if (!deletedTypes.length || !state.address) {
    return state;
  }

  const deleted = new Set(deletedTypes);
  const address = { ...state.address };

  if (deleted.has(FAVORITE_DELIVERY_TYPE.PICKUP)) {
    delete address.storeName;
    delete address.storeAddress;
  }

  if (deleted.has(FAVORITE_DELIVERY_TYPE.PICKUP_POINT)) {
    delete address.pickupPointAddress;
  }

  if (deleted.has(FAVORITE_DELIVERY_TYPE.HOME_DELIVERY)) {
    delete address.deliveryAddress;
  }

  const hasAddressData = Boolean(
    address.deliveryAddress || address.storeName || address.storeAddress || address.pickupPointAddress,
  );

  return {
    ...state,
    address: hasAddressData ? address : null,
  };
};

export const applyFavoriteAddressConfirm = (
  state: FavoriteAddressState,
  { deliveryType, deletedAddressTypes }: ApplyFavoriteAddressPayload,
  checkoutInfo: CheckoutInfo | null,
): { state: FavoriteAddressState; checkoutInfo: CheckoutInfo | null } => {
  const nextCheckoutInfo = applyCheckoutInfoDeletions(checkoutInfo, deletedAddressTypes);
  const stateAfterDeletion = applyAddressDeletionsToState(state, deletedAddressTypes);
  const nextDeliveryType = deletedAddressTypes.includes(
    deliveryType as ApplyFavoriteAddressPayload['deletedAddressTypes'][number],
  )
    ? FAVORITE_DELIVERY_TYPE.REGION
    : deliveryType;

  return {
    checkoutInfo: nextCheckoutInfo ? { ...nextCheckoutInfo, deliveryType: nextDeliveryType } : null,
    state: applyFavoriteAddressSelection(stateAfterDeletion, nextDeliveryType, nextCheckoutInfo),
  };
};
